import {PageLayout} from '@primer/react'

import {CollapsibleSidebar} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {useCollapsibleSidebarState} from '@github-ui/layout-primitives/use-collapsible-sidebar-state'

import styles from './ThreePanesLayout.module.css'

export type PaneWithAriaLabel = {
  element: React.ReactElement
  ariaLabel: string
}

export interface CollapsibleLeftPaneConfig {
  /**
   * Render function that receives the current expanded state and a toggle.
   */
  render: (args: {isExpanded: boolean; toggle: () => void}) => React.ReactElement
  /**
   * Optional footer content pinned to the bottom of the sidebar.
   * Receives the same state as `render`. Typically contains a
   * `<CollapsibleSidebar.Toggle />` and/or a feedback link.
   */
  renderFooter?: (args: {isExpanded: boolean; toggle: () => void}) => React.ReactNode
  /**
   * The width of the left pane when expanded. Defaults to `small`.
   */
  expandedWidth?: 'small' | 'medium' | 'large'
  /**
   * The sessionStorage key used to persist the expanded/collapsed state.
   */
  storageKey: string
  /**
   * The expanded state to use when there is no persisted value. Defaults to `true`.
   */
  defaultExpanded?: boolean
}

export type ThreePanesLayoutProps = {
  /**
   * Fixed non-collapsible left pane. Mutually exclusive with
   * `collapsibleLeftPane` — callers should provide at most one. (Enforcement
   * via a discriminated union was considered but rejected because the
   * helper hooks that produce these props return one-or-the-other at runtime
   * and widen both to `| undefined`, which a strict XOR rejects at call
   * sites. Consumers must uphold mutual exclusivity manually.)
   */
  leftPane?: PaneWithAriaLabel
  /**
   * If provided, renders the left pane as a `CollapsibleSidebar` (with an
   * icon-only condensed mode and persisted expanded state) instead of a
   * resizable `PageLayout.Pane`. Mutually exclusive with `leftPane`.
   */
  collapsibleLeftPane?: CollapsibleLeftPaneConfig
  middlePane?: React.ReactElement
  contentAs?: React.ElementType
  rightPane?: PaneWithAriaLabel
  rightPanePadding?: boolean
  rightPaneAs?: React.ElementType
  resizeable?: boolean
  leftPaneWidth?: 'small' | 'medium' | 'large'
}

function CollapsibleLeftPane({config}: {config: CollapsibleLeftPaneConfig}) {
  const {isExpanded, toggle} = useCollapsibleSidebarState({
    storageKey: config.storageKey,
    defaultExpanded: config.defaultExpanded,
  })
  const stateArgs = {isExpanded, toggle}
  return (
    <CollapsibleSidebar
      isExpanded={isExpanded}
      expandedWidth={config.expandedWidth ?? 'small'}
      className="d-none d-md-flex"
      footer={config.renderFooter?.(stateArgs)}
    >
      {config.render(stateArgs)}
    </CollapsibleSidebar>
  )
}

export function ThreePanesLayout({
  leftPane,
  collapsibleLeftPane,
  middlePane,
  contentAs,
  rightPane,
  rightPanePadding = true,
  rightPaneAs,
  resizeable = true,
  leftPaneWidth = 'large',
}: ThreePanesLayoutProps) {
  return (
    <PageLayout containerWidth="full" columnGap="none" className={styles.ThreePanesLayout}>
      {collapsibleLeftPane ? (
        <CollapsibleLeftPane config={collapsibleLeftPane} />
      ) : (
        leftPane && (
          <PageLayout.Pane
            width={leftPaneWidth}
            position="start"
            divider="line"
            padding="condensed"
            resizable={resizeable}
            widthStorageKey="hyperlist.pane-sidewidth"
            aria-label={leftPane.ariaLabel}
            className={styles.ThreePanesLayoutLeftPane}
          >
            {leftPane.element}
          </PageLayout.Pane>
        )
      )}
      <PageLayout.Content as={contentAs} className={collapsibleLeftPane ? styles.hasStickySidebar : undefined}>
        {rightPane ? (
          <PageLayout containerWidth="full" columnGap="none" className={styles.ThreePanesLayoutNested}>
            {middlePane && (
              <PageLayout.Pane
                width="large"
                divider="line"
                resizable={resizeable}
                widthStorageKey="hyperlist.pane-details-width"
                padding="condensed"
                position="start"
                aria-label={rightPane.ariaLabel}
                className={styles.ThreePanesLayoutMiddlePane}
              >
                {middlePane}
              </PageLayout.Pane>
            )}
            {rightPane && (
              <PageLayout.Content as={rightPaneAs} padding={rightPanePadding ? 'normal' : 'none'}>
                {rightPane.element}
              </PageLayout.Content>
            )}
          </PageLayout>
        ) : middlePane ? (
          <div className={styles.ThreePanesLayoutMiddleOnlyPane}>{middlePane}</div>
        ) : null}
      </PageLayout.Content>
    </PageLayout>
  )
}
